import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import {
  propTypes,
  AVAILABILITY_MULTIPLE_SEATS,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_FIXED,
  LINE_ITEM_ITEM,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { listingTimeZone, viewerDisplayTimeZone } from '../../util/dates';
import { createSlug, parse, stringify } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import { TALK_SESSION_LISTING_TYPE } from '../../util/sessionPricing';
import SkillBookingPanel from './SkillBookingPanel/SkillBookingPanel';
import OfferMenu from './OfferMenu/OfferMenu';
import { viewListeningProfileRoute } from '../../util/ownListeningProfile';
import {
  LISTING_PAYOUTS_UNKNOWN,
  listingAcceptsPaidBookings,
  listingPayoutsStatus,
} from '../../util/stripeErrors';
import { hasRecentRateLimit } from '../../util/sharetribeRateLimit';
import {
  closeBookATimePicker,
  isBookATimeSearchParam,
  isStartNowSearchParam,
  listingBookingMode,
  openBookATime,
  openStartNow,
  redirectGuestToSignupToBook,
  redirectGuestToTalkFree,
} from '../../util/bookingIntent';
import { isUserIdentityVerified } from '../../util/identity';
import {
  OFFER,
  REQUEST,
  getSupportedProcessesInfo,
  isBookingProcess,
  isNegotiationProcess,
  isInquiryProcess,
  isPurchaseProcess,
  isDownloadProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import {
  ModalInMobile,
  NamedLink,
  PrimaryButton,
  SecondaryButton,
  AvatarSmall,
  H1,
  H2,
} from '../../components';
import VerifiedBadge from '../VerifiedBadge/VerifiedBadge';
import AvailableNowBadge from '../AvailableNowBadge/AvailableNowBadge';
import IdentityRequiredNotice from '../IdentityRequiredNotice/IdentityRequiredNotice';
import { useFreeForListener } from '../FirstSessionFree/FirstSessionFree';
import SuspendedNotice from '../SuspendedNotice/SuspendedNotice';
import { useSafetySuspended } from '../../util/safetySuspended';
import TalkerBookingPanel from './TalkerBookingPanel/TalkerBookingPanel';
import usePresenceMap from '../../util/usePresenceMap';
import { canStartNowWithListener, isListingOnline } from '../../util/presence';
import {
  filterTimeSlotStateByTalkerAvailability,
  talkerAvailabilityFromUser,
} from '../../util/talkerAvailability';
import PriceVariantPicker from './PriceVariantPicker/PriceVariantPicker';
import SessionFormatPicker from './SessionFormatPicker/SessionFormatPicker';
import SubmitFinePrint from './SubmitFinePrint/SubmitFinePrint';

import css from './OrderPanel.module.css';

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const BookingFixedDurationForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingFixedDurationForm" */ './BookingFixedDurationForm/BookingFixedDurationForm'
  )
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

const DigitalDownloadForm = loadable(() =>
  import(/* webpackChunkName: "DigitalDownloadForm" */ './DigitalDownloadForm/DigitalDownloadForm')
);

const NegotiationForm = loadable(() =>
  import(/* webpackChunkName: "NegotiationForm" */ './NegotiationForm/NegotiationForm')
);

const NegotiationRequestQuoteForm = loadable(() =>
  import(
    /* webpackChunkName: "NegotiationRequestQuoteForm" */ './NegotiationRequestQuoteForm/NegotiationRequestQuoteForm'
  )
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();
const ORDER_PANEL_SUBMIT_BUTTON_ID = 'orderPanelSubmitButton';

const isPublishedListing = listing => {
  return listing.attributes.state === LISTING_STATE_PUBLISHED;
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const getCheapestPriceVariant = (priceVariants = []) => {
  return priceVariants.reduce((cheapest, current) => {
    return current.priceInSubunits < cheapest.priceInSubunits ? current : cheapest;
  }, priceVariants[0]);
};

const formatMoneyIfSupportedCurrency = (price, intl) => {
  try {
    return formatMoney(intl, price);
  } catch (e) {
    return `(${price.currency})`;
  }
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  closeBookATimePicker(history, location);
};

const isBookLaterParam = value =>
  value === true || value === 'true' || value === '1' || value === 1;

const setBookLaterInSearch = (history, location, bookLater) => {
  const { pathname, search, state } = location;
  const params = parse(search);
  if (/\/book\/?$/.test(pathname || '')) {
    history.push(
      `${pathname}?${stringify({ ...params, mode: bookLater ? 'later' : 'now' })}`,
      state
    );
    return;
  }
  if (bookLater) {
    history.push(`${pathname}?${stringify({ ...params, bookLater: true })}`, state);
    return;
  }
  const { bookLater: _omit, ...rest } = params;
  const searchString = stringify(rest);
  history.push(searchString ? `${pathname}?${searchString}` : pathname, state);
};

const handleSubmit = (isOwnListing, isClosed, isDirectSubmit, onSubmit, history, location) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isDirectSubmit
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
  } = props;
  const { listingType, unitType } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const hasMultiplePriceVariants = publicData?.priceVariants?.length > 1;

  if (!showPrice || !price || (isPriceVariationsInUse && hasMultiplePriceVariants)) {
    return null;
  }

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency, intl);
  const priceValue = (
    <span className={css.priceValue}>{formatMoneyIfSupportedCurrency(price, intl)}</span>
  );
  const pricePerUnit = (
    <span className={css.perUnit}>
      <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
    </span>
  );

  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValueInCTA} title={priceTitle}>
        <FormattedMessage
          id="OrderPanel.priceInMobileCTA"
          values={{ priceValue: formattedPrice }}
        />
      </div>
      <div className={css.perUnitInCTA}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      <p className={css.price}>
        <FormattedMessage id="OrderPanel.price" values={{ priceValue, pricePerUnit }} />
      </p>
    </div>
  );
};

const PriceMissing = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceMissing" />
    </p>
  );
};
const InvalidCurrency = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
    </p>
  );
};

const InvalidPriceVariants = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceVariantsAreInvalid" />
    </p>
  );
};

const hasUniqueVariants = priceVariants => {
  const priceVariantsSlugs = priceVariants?.map(variant =>
    variant.name ? createSlug(variant.name) : 'no-name'
  );
  return new Set(priceVariantsSlugs).size === priceVariants.length;
};

const hasValidPriceVariants = priceVariants => {
  const isArray = Array.isArray(priceVariants);
  const hasItems = isArray && priceVariants.length > 0;
  const variantsHaveNames = hasItems && priceVariants.every(variant => variant.name);
  const namesAreUnique = hasItems && hasUniqueVariants(priceVariants);

  return variantsHaveNames && namesAreUnique;
};

/**
 * @typedef {Object} ListingTypeConfig
 * @property {string} listingType - The type of the listing
 * @property {string} transactionType - The type of the transaction
 * @property {string} transactionType.process - The process descriptionof the transaction
 * @property {string} transactionType.alias - The alias of the transaction process
 * @property {string} transactionType.unitType - The unit type of the transaction
 */

/**
 * OrderPanel is a component that renders a panel for making bookings, purchases, or inquiries for a listing.
 * It handles different transaction processes and displays appropriate forms based on the listing type.
 *
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overwrites the default class for the root element
 * @param {string} [props.className] - Custom class that extends
 * @param {string} [props.titleClassName] - Custom class name for the title
 * @param {propTypes.listing} props.listing - The listing data (either regular or own listing)
 * @param {Array<ListingTypeConfig>} props.validListingTypes - Array of valid listing type configurations
 * @param {boolean} [props.isOwnListing=false] - Whether the listing belongs to the current user
 * @param {listingType.user|listingType.currentUser} props.author - The listing author's user data
 * @param {ReactNode} [props.authorLink] - Custom component for rendering the author link
 * @param {ReactNode} [props.payoutDetailsWarning] - Warning message about payout details
 * @param {Function} props.onSubmit - Handler for form submission
 * @param {ReactNode|string} props.title - Title of the panel
 * @param {ReactNode} [props.titleDesktop] - Alternative title for desktop view
 * @param {ReactNode|string} [props.subTitle] - Subtitle text
 * @param {Function} props.onManageDisableScrolling - Handler for managing scroll behavior
 * @param {Function} props.onFetchTimeSlots - Handler for fetching available time slots
 * @param {Object} [props.monthlyTimeSlots] - Available time slots by month
 * @param {Function} props.onFetchTransactionLineItems - Handler for fetching transaction line items
 * @param {Function} [props.onContactUser] - Handler for contacting the listing author
 * @param {Array} [props.lineItems] - Array of line items for the transaction
 * @param {boolean} props.fetchLineItemsInProgress - Whether line items are being fetched
 * @param {Object} [props.fetchLineItemsError] - Error object if line items fetch failed
 * @param {string} props.marketplaceCurrency - The currency used in the marketplace
 * @param {number} props.dayCountAvailableForBooking - Number of days available for booking
 * @param {string} props.marketplaceName - Name of the marketplace
 * @param {'h2'|'h3'} [props.sectionHeadingAs='h3'] - Semantic heading level for form section titles (under the panel title)
 *
 * @returns {JSX.Element} Component that displays the order panel with appropriate form
 */
const OrderPanel = props => {
  const [mounted, setMounted] = useState(false);
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();
  const searchParams = parse(location.search);
  const bookingMode = listingBookingMode(searchParams);
  const bookLaterFromUrl =
    bookingMode === 'later' ||
    isBookLaterParam(searchParams.bookLater) ||
    isBookATimeSearchParam(searchParams.book);
  const startNowFromUrl = bookingMode === 'now' || isStartNowSearchParam(searchParams.startNow);
  const [bookLater, setBookLater] = useState(bookLaterFromUrl);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setBookLater(bookLaterFromUrl);
  }, [bookLaterFromUrl]);
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    timeSlotsForDate,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    showListingImage,
    hideAuthorInfo,
    hidePrice,
    sectionHeadingAs = 'h3',
    currentUser,
    bookingPage = false,
  } = props;
  // Suspended (Community Guidelines): block booking before any other gate.
  const accountSuspended = useSafetySuspended(!!currentUser);

  const publicData = listing?.attributes?.publicData || {};
  const { listingType, unitType, transactionProcessAlias = '', priceVariants } = publicData || {};
  const startTimeInterval =
    publicData.startTimeInterval ||
    (listingType === TALK_SESSION_LISTING_TYPE ? 'quarterHour' : undefined);

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  // Sam Nov 2 2026: older listings (e.g. EmberElizabeth) may arrive without attributes.price
  // in the store but still carry priceVariants; fall back so they book as listening.
  const priceVariantsFallback = listing?.attributes?.publicData?.priceVariants || [];
  const minVariantSubunits = priceVariantsFallback
    .map(v => v?.priceInSubunits)
    .filter(n => typeof n === 'number' && n > 0)
    .sort((a, b) => a - b)[0];
  const price =
    listing?.attributes?.price ||
    (minVariantSubunits ? new sdkTypes.Money(minVariantSubunits, marketplaceCurrency) : null);
  const isInquiry = isInquiryProcess(processName);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const isDownload = isDownloadProcess(processName);
  const isPaymentProcess = isBooking || isPurchase || isNegotiation || isDownload;

  const showPriceMissing = isPaymentProcess && !isNegotiation && !price;
  const showInvalidCurrency =
    isPaymentProcess && !isNegotiation && price?.currency !== marketplaceCurrency;

  const listingTimeZoneName = listingTimeZone(listing?.attributes?.availabilityPlan?.timezone);
  // Booking instants stay on the listing clock (DST-aware). Viewer TZ is
  // display-only — using EST here labeled Lauren's 9 AM slot as 8 AM.
  const timeZone =
    listingTimeZoneName || (mounted ? viewerDisplayTimeZone('America/New_York') : null);
  const talkerPlan = talkerAvailabilityFromUser(currentUser);
  const monthlyTimeSlotsForTalker = filterTimeSlotStateByTalkerAvailability(
    monthlyTimeSlots,
    talkerPlan
  );
  const timeSlotsForDateForTalker = filterTimeSlotStateByTalkerAvailability(
    timeSlotsForDate,
    talkerPlan
  );
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const shouldHaveFixedBookingDuration = isBooking && [LINE_ITEM_FIXED].includes(lineItemUnitType);
  const showBookingFixedDurationForm =
    mounted && shouldHaveFixedBookingDuration && !isClosed && timeZone && priceVariants?.length > 0;

  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = mounted && shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = mounted && shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const shouldHavePurchase = isPurchase && lineItemUnitType === LINE_ITEM_ITEM;
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = shouldHavePurchase && !isClosed && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm =
    mounted && shouldHavePurchase && !isClosed && typeof currentStock === 'number';

  const showDownloadForm = mounted && !isClosed && isDownload;
  const showInquiryForm = mounted && !isClosed && isInquiry;
  // if listing is a request, we show the negotiation form (reverse negotiation). User (provider) needs to make an offer first.
  const showNegotiationForm = mounted && !isClosed && isNegotiation && unitType === REQUEST;
  // if listing is an offer, we show the "request a quote" form as user needs to ask for a quote first from the provider.
  const showRequestQuoteForm = mounted && !isClosed && isNegotiation && unitType === OFFER;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};

  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const bookFromUrl = isBookATimeSearchParam(searchParams.book);
  const isOrderOpen = !!searchParams.orderOpen || bookFromUrl || bookingPage || !!bookingMode;
  const preselectedPriceVariantSlug = searchParams.bookableOption;

  const seatsEnabled = [AVAILABILITY_MULTIPLE_SEATS].includes(listingTypeConfig?.availabilityType);

  // Note: publicData contains priceVariationsEnabled if listing is created with priceVariations enabled.
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const preselectedPriceVariant =
    Array.isArray(priceVariants) && preselectedPriceVariantSlug && isPriceVariationsInUse
      ? priceVariants.find(pv => pv?.name && createSlug(pv?.name) === preselectedPriceVariantSlug)
      : null;

  const isTalkSession = listingType === TALK_SESSION_LISTING_TYPE;
  // Free first session: a signed-in talker without ID can still book (18+ step comes next).
  const freeForPanel = useFreeForListener(author?.id?.uuid || null, currentUser || null);
  const freeSetupOpen = !!(freeForPanel.free || freeForPanel.needsVerify);
  const { map: presenceMap, pausedMap = {}, busyMap = {}, nextAvailableMap = {} } = usePresenceMap(
    listing?.id?.uuid ? [listing.id.uuid] : []
  );
  // Persisted Available flag (plus live heartbeat). Seed listings stay offline.
  const listenerOnline = isTalkSession && isListingOnline(listing?.id?.uuid, presenceMap, listing);
  // Show the Start now block whenever this listener is actually online.
  // Own listings still render the owner note (no self-book CTA).
  const payoutsStatus = listingPayoutsStatus(listing);
  const payoutsReady = listingAcceptsPaidBookings(listing);
  const isGuest = !currentUser;
  const showPayoutsBusy =
    isTalkSession && !isGuest && payoutsStatus === LISTING_PAYOUTS_UNKNOWN && hasRecentRateLimit();
  const showPayoutsBlocked = isTalkSession && !payoutsReady && !isGuest && !showPayoutsBusy;
  // Guests still see Start now / Book a time so they can Sign up. Signed-in
  // users keep the payouts gate.
  const startNowPaused = !!pausedMap[(listing?.id?.uuid)];
  const sessionBusy = !!busyMap[(listing?.id?.uuid)];
  const nextAvailableAt = nextAvailableMap[(listing?.id?.uuid)] || null;
  const showStartNowPanel =
    isTalkSession &&
    !isClosed &&
    (payoutsReady || isGuest) &&
    (listenerOnline || startNowFromUrl) &&
    !startNowPaused;
  const talkLater =
    isTalkSession &&
    (bookLater || bookLaterFromUrl || (!listenerOnline && !startNowFromUrl) || startNowPaused);
  const showInstantPath = showStartNowPanel && !talkLater;
  const hideScheduledFormsForInstant = showInstantPath;
  const showStartNowCta =
    canStartNowWithListener({
      isTalkSession,
      listenerOnline: listenerOnline || startNowFromUrl,
      isOwnListing,
      isClosed,
      startNowPaused,
      sessionBusy,
    }) &&
    !bookLater &&
    (payoutsReady || isGuest);
  const onStickyStartNow = () => {
    if (isGuest) {
      redirectGuestToTalkFree(history, location);
      return;
    }
    openStartNow(history, location);
  };

  // Talk listings already have one pink CTA: ListingPaidBooking on mobile
  // (Book a time or Start now) and the sidebar OrderPanel on desktop.
  // The sticky bar was a second identical full-width button. Do not mount it.
  const hideStickyBookBar = isTalkSession || isOrderOpen;

  const onOpenBookATime = () => {
    if (isGuest) {
      redirectGuestToSignupToBook(history, location);
      return;
    }
    openBookATime(history, location);
  };

  const onToggleBookLater = next => {
    setBookLater(next);
    setBookLaterInSearch(history, location, next);
  };
  // Listener-set talk prices (TEST): the picker shows this listing's prices.
  const PriceVariantField = useMemo(
    () => (isTalkSession ? fieldProps => <SessionFormatPicker {...fieldProps} listing={listing} /> : PriceVariantPicker),
    [isTalkSession, listing?.id?.uuid, JSON.stringify(listing?.attributes?.publicData?.talkPrices || null)] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const offeredFormats = publicData.offeredFormats || publicData['offered-formats'];

  const priceVariantsMaybe = isPriceVariationsInUse
    ? {
        isPriceVariationsInUse,
        priceVariants,
        priceVariantFieldComponent: PriceVariantField,
        preselectedPriceVariant: isTalkSession ? null : preselectedPriceVariant,
        isPublishedListing: isPublishedListing(listing),
        requireSessionFormat: isTalkSession,
        offeredFormats,
      }
    : !isPriceVariationsInUse && showBookingFixedDurationForm
    ? {
        isPriceVariationsInUse: false,
        priceVariants: [getCheapestPriceVariant(priceVariants)],
        priceVariantFieldComponent: PriceVariantField,
        requireSessionFormat: isTalkSession,
        offeredFormats,
      }
    : {};

  const showInvalidPriceVariantsMessage =
    isPriceVariationsInUse && !hasValidPriceVariants(priceVariants);

  const sharedProps = {
    lineItemUnitType,
    onSubmit,
    price,
    marketplaceCurrency,
    listingId: listing.id,
    listing,
    isOwnListing,
    currentUser,
    marketplaceName,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    sectionHeadingAs,
  };

  const showClosedListingHelpText = listing.id && isClosed;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={classNames(css.modalContainer, {
          [css.modalContainerInline]: bookingPage,
        })}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => {
          closeOrderModal(history, location);
          document.getElementById(ORDER_PANEL_SUBMIT_BUTTON_ID)?.focus();
        }}
        showAsModalMaxWidth={bookingPage ? 0 : MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        {bookingPage ? null : (
          <div className={css.modalHeading}>
            <H1 className={css.heading}>{title}</H1>
          </div>
        )}

        {!bookingPage && showListingImage ? (
          <div className={css.orderHeading}>
            {titleDesktop ? titleDesktop : <H2 className={titleClasses}>{title}</H2>}
            {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
          </div>
        ) : null}
        {!isTalkSession && !hidePrice && (
          <PriceMaybe
            price={price}
            publicData={publicData}
            validListingTypes={validListingTypes}
            intl={intl}
            marketplaceCurrency={marketplaceCurrency}
          />
        )}

        {!isTalkSession && !hideAuthorInfo && (
          <div className={css.author}>
            <AvatarSmall
              user={author}
              className={css.providerAvatar}
              profileRoute={viewListeningProfileRoute(listing)}
              online={listenerOnline}
            />
            <span className={css.providerNameLinked}>
              <FormattedMessage id="OrderPanel.author" values={{ name: authorLink }} />
              <VerifiedBadge user={author} />
              <AvailableNowBadge online={listenerOnline} />
            </span>
            <span className={css.providerNamePlain}>
              <FormattedMessage id="OrderPanel.author" values={{ name: authorDisplayName }} />
              <VerifiedBadge user={author} />
              <AvailableNowBadge online={listenerOnline} />
            </span>
          </div>
        )}

        {/* Sam 7:41 PM Oct 23: keep format/time visible; the ID + card gate runs at Book (checkout → setup). */}
        {isTalkSession && !accountSuspended && isUserIdentityVerified(author) && currentUser && !isUserIdentityVerified(currentUser) && !require('../../util/adultSelfAttest').isAdultSelfAttestedUser(currentUser) && !freeSetupOpen ? (
          <IdentityRequiredNotice context="book" />
        ) : null}
        {isTalkSession && accountSuspended ? (
          <SuspendedNotice />
        ) : isTalkSession && !isUserIdentityVerified(author) ? (
          <IdentityRequiredNotice context="listing" />
        ) : showPayoutsBusy ? (
          <div className={css.orderHelp} data-testid="listing-payouts-busy">
            <p>
              <FormattedMessage id="ListingPaidBooking.payoutsBusy" />
            </p>
            <SecondaryButton type="button" onClick={() => window.location.reload()}>
              <FormattedMessage id="ListingPaidBooking.payoutsBusyCta" />
            </SecondaryButton>
          </div>
        ) : showPayoutsBlocked ? (
          <div className={css.orderHelp} data-testid="listing-payouts-not-ready">
            <p>
              <FormattedMessage
                id={
                  isOwnListing
                    ? 'ListingPaidBooking.payoutsNotReadyOwn'
                    : 'ListingPaidBooking.payoutsNotReady'
                }
              />
            </p>
            {isOwnListing ? (
              <NamedLink name="StripePayoutPage">
                <FormattedMessage id="ListingPaidBooking.payoutsNotReadyOwnCta" />
              </NamedLink>
            ) : null}
          </div>
        ) : showPriceMissing ? (
          <PriceMissing />
        ) : showInvalidCurrency ? (
          <InvalidCurrency />
        ) : showInvalidPriceVariantsMessage ? (
          <InvalidPriceVariants />
        ) : isTalkSession && isOwnListing ? (
          <TalkerBookingPanel listing={listing} author={author} isOwnListing />
        ) : isTalkSession && (payoutsReady || isGuest) && !isClosed && searchParams.learn && typeof searchParams.offer !== 'string' ? (
          // Sam Nov 2 2026: generic Learn opens "Pick a skill" (no first-skill lock).
          <SkillBookingPanel
            listing={listing}
            online={!!(listenerOnline || startNowFromUrl)}
            onSubmit={onSubmit}
            timeZone={timeZone}
          />
        ) : isTalkSession && (payoutsReady || isGuest) && !isClosed ? (
          <>
          <OfferMenu
            listing={listing}
            initialOfferId={typeof searchParams.offer === 'string' ? searchParams.offer : null}
            renderOffer={offer => (
              <SkillBookingPanel
                key={offer.id}
                listing={listing}
                offerId={offer.id}
                excludeMinutes={[]}
                online={!!(listenerOnline || startNowFromUrl)}
                onSubmit={onSubmit}
                timeZone={timeZone}
              />
            )}
            // One panel per path (Sam, Oct 22 2026): listening uses the same date/time check as skills.
            renderListening={() => null}
            renderListeningOld={() => (
          <TalkerBookingPanel
            listing={listing}
            author={author}
            currentUser={currentUser}
            isOwnListing={false}
            online={!!(listenerOnline || startNowFromUrl)}
            startNowPaused={startNowPaused}
            sessionBusy={sessionBusy}
            nextAvailableAt={nextAvailableAt}
            onSubmit={onSubmit}
            timeZone={timeZone}
            bookLater={talkLater}
            onToggleBookLater={onToggleBookLater}
            hideIdentity={bookingPage}
            laterForm={
              showBookingFixedDurationForm ? (
                <BookingFixedDurationForm
                  seatsEnabled={seatsEnabled}
                  className={css.bookingForm}
                  formId="OrderPanelBookingFixedDurationForm"
                  dayCountAvailableForBooking={dayCountAvailableForBooking}
                  monthlyTimeSlots={monthlyTimeSlotsForTalker}
                  timeSlotsForDate={timeSlotsForDateForTalker}
                  onFetchTimeSlots={onFetchTimeSlots}
                  startDatePlaceholder={intl.formatMessage({
                    id: 'BookingFixedDurationForm.datePlaceholder',
                  })}
                  startTimeInterval={startTimeInterval}
                  timeZone={timeZone}
                  preselectedStartMs={Number(searchParams.next) || nextAvailableAt || null}
                  finePrintComponent={props => <SubmitFinePrint {...props} hideNoShowPolicy />}
                  {...priceVariantsMaybe}
                  {...sharedProps}
                />
              ) : null
            }
          />
            )}
          />
          </>
        ) : showBookingFixedDurationForm && !hideScheduledFormsForInstant ? (
          <BookingFixedDurationForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingFixedDurationForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            monthlyTimeSlots={monthlyTimeSlotsForTalker}
            timeSlotsForDate={timeSlotsForDateForTalker}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={
              isTalkSession
                ? intl.formatMessage({ id: 'BookingFixedDurationForm.datePlaceholder' })
                : intl.formatDate(TODAY, dateFormattingOptions)
            }
            startTimeInterval={startTimeInterval}
            timeZone={timeZone}
            finePrintComponent={SubmitFinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : showBookingTimeForm && !hideScheduledFormsForInstant ? (
          <BookingTimeForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingTimeForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            monthlyTimeSlots={monthlyTimeSlotsForTalker}
            timeSlotsForDate={timeSlotsForDateForTalker}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            timeZone={timeZone}
            finePrintComponent={SubmitFinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : showBookingDatesForm && !hideScheduledFormsForInstant ? (
          <BookingDatesForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingDatesForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            timeZone={timeZone}
            finePrintComponent={SubmitFinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : showProductOrderForm ? (
          <ProductOrderForm
            formId="OrderPanelProductOrderForm"
            currentStock={currentStock}
            allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
            pickupEnabled={pickupEnabled && displayPickup}
            shippingEnabled={shippingEnabled && displayShipping}
            displayDeliveryMethod={displayPickup || displayShipping}
            onContactUser={onContactUser}
            {...sharedProps}
          />
        ) : showDownloadForm ? (
          <DigitalDownloadForm
            formId="OrderPanelDigitalDownloadForm"
            finePrintComponent={SubmitFinePrint}
            isOwnListing={isOwnListing}
            {...sharedProps}
          />
        ) : showInquiryForm ? (
          <InquiryWithoutPaymentForm
            formId="OrderPanelInquiryForm"
            onSubmit={onSubmit}
            finePrintComponent={SubmitFinePrint}
            isOwnListing={isOwnListing}
          />
        ) : showNegotiationForm ? (
          <NegotiationForm
            formId="OrderPanelNegotiationForm"
            onSubmit={onSubmit}
            finePrintComponent={SubmitFinePrint}
            payoutDetailsWarning={payoutDetailsWarning}
            isOwnListing={isOwnListing}
          />
        ) : showRequestQuoteForm ? (
          <NegotiationRequestQuoteForm
            formId="OrderPanelRequestQuoteForm"
            onSubmit={onSubmit}
            finePrintComponent={SubmitFinePrint}
            payoutDetailsWarning={payoutDetailsWarning}
            isOwnListing={isOwnListing}
          />
        ) : !isKnownProcess ? (
          <p className={css.errorSidebar}>
            <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
          </p>
        ) : null}
      </ModalInMobile>
      {showStartNowCta || hideStickyBookBar ? null : (
        <div
          className={classNames(css.openOrderForm, {
            [css.openOrderFormHidden]: hideStickyBookBar,
          })}
          hidden={hideStickyBookBar}
        >
          <PriceMaybe
            price={price}
            publicData={publicData}
            validListingTypes={validListingTypes}
            intl={intl}
            marketplaceCurrency={marketplaceCurrency}
            showCurrencyMismatch
          />

          {isClosed ? (
            <div className={css.closedListingButton}>
              <FormattedMessage id="OrderPanel.closedListingButtonText" />
            </div>
          ) : showPayoutsBusy ? (
            <p className={css.orderHelp} data-testid="listing-payouts-busy">
              <FormattedMessage id="ListingPaidBooking.payoutsBusy" />
            </p>
          ) : showPayoutsBlocked ? (
            <p className={css.orderHelp} data-testid="listing-payouts-not-ready">
              <FormattedMessage
                id={
                  isOwnListing
                    ? 'ListingPaidBooking.payoutsNotReadyOwn'
                    : 'ListingPaidBooking.payoutsNotReady'
                }
              />
            </p>
          ) : (
            <PrimaryButton
              id={ORDER_PANEL_SUBMIT_BUTTON_ID}
              onClick={
                showStartNowCta
                  ? onStickyStartNow
                  : isTalkSession
                  ? onOpenBookATime
                  : isGuest
                  ? () => redirectGuestToSignupToBook(history, location)
                  : handleSubmit(
                      isOwnListing,
                      isClosed,
                      showInquiryForm || showNegotiationForm,
                      onSubmit,
                      history,
                      location
                    )
              }
              disabled={isOutOfStock}
            >
              {showStartNowCta ? (
                <FormattedMessage id={currentUser ? 'StartNowPanel.cta' : 'StartNowPanel.logIn'} />
              ) : isBooking ? (
                <FormattedMessage
                  id={
                    currentUser
                      ? 'OrderPanel.ctaButtonMessageBooking'
                      : 'ListingPaidBooking.bookCtaGuest'
                  }
                />
              ) : isOutOfStock ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
              ) : isPurchase ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
              ) : showNegotiationForm ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageMakeOffer" />
              ) : showRequestQuoteForm ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageRequestAQuote" />
              ) : showDownloadForm ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageDownload" />
              ) : (
                <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
              )}
            </PrimaryButton>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderPanel;
